/**
 * useRegenerate — 消息重新生成/重试逻辑
 * 从 OpsMateContext 中抽取
 */
import { useCallback, useRef } from "react";
import { App } from "antd";
import { createSessionLogStream, searchSessionLog } from "@/services/opsmate/sessionLog";
import { getDocumentsByIds } from "@/services/opsmate/documents";
import { createConReply } from "@/services/opsmate/conReply";
import { saveBulkCreateConCitation } from "@/services/opsmate/conCitation";
import { formatFileSize } from "@/utils/formatters";
import { parseFinalAnswer, extractStreamingAnswerView } from "@/utils/answer";
import { createStreamTypewriter } from "@/utils/streamTypewriter";
import { createFrameThrottle } from "@/utils/frameThrottle";
import { isStreamSmoothingEnabled } from "@/utils/streamDisplayConfig";
import {
  normalizeReferences,
  mergeReferenceEventMetadata,
  buildDocumentsFromReferences,
  getReferenceRelatedDocIds,
  matchDocInfoByRelatedIds,
  getDocInfoDocumentGuid,
  getDocInfoDataSource,
  getDocInfoCategoryIds,
  getDocInfoSourcePathAskId,
  isDocInfoValid,
} from "@/utils/conversation";
import { splitReferencesFence } from "@/utils/referencesFence";
import { buildQuestionScopeParams, canSubmitQuestionScope } from "./useSubmitMessage.scope";

interface UseRegenerateParams {
  itemsRef: React.MutableRefObject<any[]>;
  selectedKeys: string;
  onlyUseSelectedSources: boolean;
  selectedSourceGuidNames: string[];
  docCategoryIds: string[];
  studySourceIds: string[];
  includePersonalDocs: boolean;
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
}

export function useRegenerate({
  itemsRef,
  selectedKeys,
  onlyUseSelectedSources,
  selectedSourceGuidNames,
  docCategoryIds,
  studySourceIds,
  includePersonalDocs,
  setItems,
}: UseRegenerateParams) {
  const { message } = App.useApp();
  const messageRef = useRef(message);
  messageRef.current = message;

  const regenerateMessage = useCallback(
    async (aiMessageKey: string) => {
      // 找到该 AI 消息对应的上一条用户消息
      const allItems = itemsRef.current;
      const aiIndex = allItems.findIndex((item) => item.key === aiMessageKey);
      if (aiIndex < 0) return;

      let userQuestion = "";
      for (let i = aiIndex - 1; i >= 0; i--) {
        if (allItems[i].role === "user") {
          userQuestion = allItems[i].content;
          break;
        }
      }
      if (!userQuestion) {
        messageRef.current.warning("Cannot find the original question to regenerate.");
        return;
      }

      // 获取当前会话 ID
      const activeConversationId = selectedKeys && selectedKeys !== "add" ? selectedKeys : null;
      if (!activeConversationId) {
        messageRef.current.warning("No active conversation.");
        return;
      }
      if (!canSubmitQuestionScope(onlyUseSelectedSources, selectedSourceGuidNames)) {
        messageRef.current.warning("Please select at least one source document.");
        return;
      }

      // 将 AI 消息设回 loading 状态
      setItems((prev) =>
        prev.map((item) =>
          item.key === aiMessageKey
            ? { ...item, status: "loading", loading: true, content: "" }
            : item,
        ),
      );

      try {
        // 获取历史 session log id 列表
        let historySessionLogIdList: string[] = [];
        try {
          const historyResp: any = await searchSessionLog({
            ConversationID: String(activeConversationId),
            Page: 1,
            Limit: 100,
          });
          const rawList: any[] = historyResp.Data;
          historySessionLogIdList = rawList
            .filter((row: any) => String(row.SessionLog_QuestionType) === "0")
            .map((row: any) => String(row.ID ?? ""))
            .filter((id: string) => !!id)
            .slice(0, 100);
        } catch {
          historySessionLogIdList = [];
        }

        const questionScopeParams = buildQuestionScopeParams({
          onlyUseSelectedSources,
          selectedSourceGuidNames,
          docCategoryIds,
          studySourceIds,
          includePersonalDocs,
        });
        const sessionLogParams = {
          question: userQuestion,
          conversationId: activeConversationId,
          HistorySessionLogIdList: historySessionLogIdList,
          ...questionScopeParams,
        };

        // ==================== 流式调用（PikeRag v2 SSE） ====================
        let streamedAnswer = "";
        let finalAnswerFromEvent = "";
        // AI Reasoning 区域读取 rationale 内容
        let rationaleText = "";
        // Summary 区域当前无单独来源，保持为空（V3 不产出独立 summary 字段）
        const summaryText = "";
        // references = 最终引用清单；eventReferences 缓存 event: references 的元数据，
        // 流结束后与内联 ```references 解析出的清单合并（内联块建表，事件补元数据）。
        let references: any[] = [];
        let eventReferences: any[] = [];
        let sessionLogIdFromResp: string | undefined;
        let streamError: unknown = null;
        let finalExecutionCode: number | null = null;
        let finalExecutionMessage = "";

        const progressSteps: any[] = [];

        // 显示层平滑（打字机 + 帧节流）总开关，默认关：按上游节奏原速渲染。
        const smoothing = isStreamSmoothingEnabled();
        // 模型可能把答案以 JSON 字符串（含 answer / rationale）形式输出，
        // 流式过程中需要实时提取出内层 answer 部分用于展示
        const typewriter = createStreamTypewriter({
          enabled: smoothing,
          onTick: (visible) => {
            streamedAnswer = visible;
            // 先剥离内联 ```references 围栏，再去 JSON 外壳：顺序不能反，否则
            // ```references 会被去壳逻辑误判为 JSON 代码围栏导致正文丢失。
            // 围栏尚未闭合时打 buildingReferences 标记，并把已攒够的完整引用条目逐条增量建表，
            // 渲染层据此边构建边显示（不等整段 JSON、不等 getdocumentid）。
            const split = splitReferencesFence(streamedAnswer);
            const currentContent = extractStreamingAnswerView(split.text);
            const previewRefs =
              split.building || split.references.length > 0
                ? normalizeReferences(split.references)
                : [];
            setItems((prev) =>
              prev.map((item) =>
                item.key === aiMessageKey
                  ? {
                      ...item,
                      status: "streaming",
                      loading: false,
                      content: currentContent,
                      buildingReferences: split.building,
                      references: previewRefs,
                      documents: buildDocumentsFromReferences(previewRefs),
                      progress: [...progressSteps],
                    }
                  : item,
              ),
            );
          },
        });

        const appendStreamDelta = (delta: string) => typewriter.push(delta);

        // frame 节流批量刷新进度条，避免 SSE 每个事件触发一次 setState
        const flushProgress = createFrameThrottle(() => {
          const snapshot = [...progressSteps];
          setItems((prev) =>
            prev.map((item) =>
              item.key === aiMessageKey
                ? {
                    ...item,
                    status: item.status === "loading" ? "streaming" : item.status,
                    loading: false,
                    progress: snapshot,
                  }
                : item,
            ),
          );
        }, smoothing);

        // 当前 llm.delta reasoning 的打字机，与 content 同样的逐字效果
        let reasoningTypewriter: ReturnType<typeof createStreamTypewriter> | null = null;
        let reasoningTypewriterStep: any = null;
        const closeReasoningTypewriter = () => {
          if (reasoningTypewriter) {
            reasoningTypewriter.flush();
            reasoningTypewriter = null;
            reasoningTypewriterStep = null;
          }
        };

        const pushProgressStep = (kind: "reasoning" | "tool", evt: any) => {
          const event = String(evt?.event ?? "");
          const reasoningDelta = typeof evt?.reasoning === "string" ? evt.reasoning : "";

          if (
            kind === "reasoning" &&
            event === "llm.delta" &&
            progressSteps.length > 0 &&
            progressSteps[progressSteps.length - 1].kind === "reasoning" &&
            progressSteps[progressSteps.length - 1].event === "llm.delta" &&
            reasoningTypewriterStep === progressSteps[progressSteps.length - 1] &&
            reasoningTypewriter
          ) {
            const last = progressSteps[progressSteps.length - 1];
            last.updatedAt = Date.now();
            reasoningTypewriter.push(reasoningDelta);
          } else {
            closeReasoningTypewriter();
            const step: any = {
              kind,
              event,
              phase: evt?.phase ?? "",
              status: evt?.status ?? "",
              tool: evt?.tool ?? "",
              reasoning: "",
              payload: evt?.payload ?? null,
              raw: evt,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };
            progressSteps.push(step);

            if (kind === "reasoning" && event === "llm.delta") {
              reasoningTypewriterStep = step;
              reasoningTypewriter = createStreamTypewriter({
                enabled: smoothing,
                onTick: (visible) => {
                  step.reasoning = visible;
                  step.updatedAt = Date.now();
                  flushProgress.schedule();
                },
              });
              reasoningTypewriter.push(reasoningDelta);
            } else {
              step.reasoning = reasoningDelta;
            }
          }

          flushProgress.schedule();
        };

        // status（阶段提示）/ trace（Agent 执行轨迹）作为独立结构化步骤追加
        const pushStructuredStep = (partial: Record<string, any>) => {
          closeReasoningTypewriter();
          progressSteps.push({
            ...partial,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          });
          flushProgress.schedule();
        };

        await createSessionLogStream(sessionLogParams, {
          onContent: (delta) => appendStreamDelta(delta),
          onReasoning: (evt) => pushProgressStep("reasoning", evt),
          onTool: (evt) => pushProgressStep("tool", evt),
          onStatus: (evt) => {
            const message = String(evt?.message ?? "");
            pushStructuredStep({ kind: "status", event: "status", message });
          },
          // progress（“正在加载/组装/调用/执行工具…”）不展示。
          // trace 仅保留“带命中文档的检索结果”（kb_search 等 tool_result），用于列出引用的文档名称；
          // 其余 trace（tool_call / load_skill / 纯文本结果等）不展示。
          onTrace: (evt) => {
            const trace = evt?.trace ?? {};
            if (String(trace?.role) !== "tool_result" || !Array.isArray(trace?.toolResult)) return;
            const hasDocName = trace.toolResult.some(
              (r: any) => typeof r?.filename === "string" && r.filename.trim(),
            );
            if (!hasDocName) return;
            pushStructuredStep({ kind: "trace", event: "trace", trace });
          },
          // 实时流不再展示 trace（Agent 执行轨迹）：不订阅 onTrace，过程面板只保留
          // status / progress / reasoning。历史会话已落库的 trace 步骤仍按 ProgressLog 渲染。
          onReferences: (evt) => {
            // 上游原生字段归一化后暂存，流结束再与内联块合并补全
            eventReferences = normalizeReferences(evt?.references);
          },
          // 完成事件：status=failed / finishReason=error 视为工作流失败
          onCompleted: (evt) => {
            const status = String(evt?.status ?? "");
            const finishReason = String(evt?.finishReason ?? "");
            if (status === "failed" || finishReason === "error") {
              if (finalExecutionCode === null || finalExecutionCode === 0) finalExecutionCode = 1;
            }
          },
          // 错误事件（上游 stream.error 或控制器层 4xx/5xx）
          onStreamError: (err) => {
            const codeNum = Number(err?.code);
            finalExecutionCode = Number.isFinite(codeNum) && codeNum !== 0 ? codeNum : 1;
            if (typeof err?.message === "string" && err.message) {
              finalExecutionMessage = err.message;
            }
          },
          onAnswerSaved: (evt) => {
            sessionLogIdFromResp = evt?.session_log_id;
            if (evt && typeof evt.execution_code !== "undefined") {
              const codeNum = Number(evt.execution_code);
              finalExecutionCode = Number.isFinite(codeNum) ? codeNum : null;
            }
            if (typeof evt?.execution_message === "string") {
              finalExecutionMessage = evt.execution_message;
            }
            // 失败时后端会带回兜底答案文本；成功时为空，由流式累积内容兜底
            if (typeof evt?.answer === "string" && evt.answer) {
              finalAnswerFromEvent = evt.answer;
            }
          },
          onError: (err) => {
            streamError = err;
          },
        });

        // 流结束后立刻吐完队列剩余，让 streamedAnswer 与后续 setItems 同步
        typewriter.flush();
        closeReasoningTypewriter();
        flushProgress.flush();

        if (streamError) throw streamError;

        if (
          finalExecutionCode !== null &&
          finalExecutionCode !== 0 &&
          !streamedAnswer &&
          !finalAnswerFromEvent
        ) {
          let failureMsg = finalExecutionMessage || "工作流执行失败";
          try {
            const parsed = JSON.parse(finalExecutionMessage);
            if (parsed && typeof parsed === "object") {
              failureMsg = (parsed as any).message || (parsed as any).code || finalExecutionMessage;
            }
          } catch {
            /* ignore */
          }
          finalAnswerFromEvent = `[请求失败 ${finalExecutionCode}] ${failureMsg}`;
        }

        // 兜底解析：从 streamedAnswer 中提取 rationale 供 AI Reasoning 展示
        const parsedFromStream = parseFinalAnswer(streamedAnswer);
        if (parsedFromStream && !rationaleText && parsedFromStream.rationale) {
          rationaleText = parsedFromStream.rationale;
        }

        // 最终展示文本：优先使用失败兜底 finalAnswerFromEvent（必要时再清洗一次以剥离 JSON 外壳），
        // 否则退回到流式累积内容的清洗视图，与流式期间用户看到的保持一致。
        // 先 splitReferencesFence 剥离内联 ```references 围栏（并解析内联引用清单），
        // 再 extractStreamingAnswerView 去 JSON 外壳——顺序不能反，否则 ```references
        // 会被去壳逻辑误当 JSON 代码围栏，剥壳后找不到 answer 字段而返回空。
        const finalSplit = splitReferencesFence(finalAnswerFromEvent);
        const streamSplit = splitReferencesFence(streamedAnswer);
        const cleanedFromFinal = extractStreamingAnswerView(finalSplit.text);
        const cleanedFromStream = extractStreamingAnswerView(streamSplit.text);
        const answerText =
          cleanedFromFinal || cleanedFromStream || finalSplit.text || streamSplit.text || "";

        // 内联块建表，事件补元数据：内联 ```references 为权威清单，event: references 补全 segment 等
        const inlineReferences = normalizeReferences(streamSplit.references);
        references = inlineReferences.length
          ? mergeReferenceEventMetadata(inlineReferences, eventReferences)
          : eventReferences;

        // 构建 documents
        const docMap = new Map<string, any>();
        const refDocIdSet = new Set<string>();
        (references || []).forEach((ref: any) => {
          const id = ref?.references_document_id ?? ref?.document_id ?? "";
          const refName = (ref?.references_document_name ?? id ?? "").toString().trim();
          if (!id && !refName) return;
          const dkey = id || refName;
          // 用 relatedDocumentIds 查 getdocumentid：owner 未必落库，重复件才在本租户 DB 里。
          const relatedIds = getReferenceRelatedDocIds(ref);
          relatedIds.forEach((rid) => refDocIdSet.add(rid));
          const rawSegment = (ref?.references_segment ?? "").toString();
          let seg = rawSegment;
          const marker = "Content: ";
          const markerIndex = rawSegment.indexOf(marker);
          if (markerIndex >= 0) seg = rawSegment.slice(markerIndex + marker.length);
          const existing = docMap.get(dkey);
          if (!existing) {
            docMap.set(dkey, {
              id,
              name: refName || id,
              citations: seg || "",
              canDownload: true,
              relatedIds,
            });
          } else {
            relatedIds.forEach((rid: string) => {
              if (!existing.relatedIds.includes(rid)) existing.relatedIds.push(rid);
            });
            if (seg) {
              existing.citations = existing.citations
                ? `${existing.citations}\n\n---\n\n${seg}`
                : seg;
            }
          }
        });
        let documents = Array.from(docMap.values());

        // 批量获取文档信息
        if (refDocIdSet.size > 0) {
          try {
            const docInfoResp: any = await getDocumentsByIds(Array.from(refDocIdSet));
            const docInfoList: any[] =
              docInfoResp && Array.isArray(docInfoResp.data)
                ? docInfoResp.data
                : Array.isArray(docInfoResp)
                  ? docInfoResp
                  : [];
            documents = documents.map((doc) => {
              const matchedInfo = matchDocInfoByRelatedIds(docInfoList, doc.relatedIds, doc.name);
              // 不在 DB DocumentList 中：仍保留在 documents（正文角标 / References 区需要展示），
              // 但不打 inDocumentList 标记，右侧边栏据此过滤
              if (!matchedInfo) return doc;
              // 右侧文档清单完全以 getdocumentid 校准回的 DocumentGuid 为准：写入 guidName
              // （getDocumentGuid 优先取它），用于去重与 Only use selected sources 的 references_document_id。
              // 保留 doc.id（上游 owner 文档 ID）不动：正文角标命中与 artifact 图片鉴权仍按 owner ID。
              return {
                ...doc,
                guidName: getDocInfoDocumentGuid(matchedInfo) || undefined,
                sourcePathAskId: getDocInfoSourcePathAskId(matchedInfo) || undefined,
                docCategoryIds: getDocInfoCategoryIds(matchedInfo),
                name: matchedInfo.FileName ?? matchedInfo.fileName ?? doc.name,
                size: formatFileSize(matchedInfo.FileSize ?? matchedInfo.fileSize) || undefined,
                fileUrl: matchedInfo.DownloadURL ?? undefined,
                dataSource: getDocInfoDataSource(matchedInfo) || undefined,
                // 软删旧行（IsValid=false）仍保留 enrichment 供下载/References，但不进右侧边栏
                inDocumentList: isDocInfoValid(matchedInfo),
              };
            });
          } catch {
            /* ignore */
          }
        }

        // 创建 ConReply
        try {
          const replyResp = await createConReply({
            questionId: sessionLogIdFromResp,
            content: answerText ?? "",
          });
          const replyRaw = (replyResp as any) ?? {};
          const replyEntity = (replyRaw.Entity as any) || (replyRaw.entity as any) || replyRaw;
          const replyId =
            replyEntity?.ID ??
            replyEntity?.Id ??
            replyEntity?.id ??
            replyRaw?.ID ??
            replyRaw?.Id ??
            replyRaw?.id ??
            undefined;

          if (replyId && Array.isArray(references) && references.length > 0) {
            const bulkItems = references
              .map((ref: any) => {
                const citationId = ref?.references_segment_id ?? ref?.segment_id ?? "";
                const citationContent = ref?.references_segment ?? ref?.segment_content ?? "";
                const documentId = ref?.references_document_id ?? ref?.document_id ?? "";
                const position = ref?.references_segment_position ?? ref?.segment_position ?? "";
                // v2 不返回 segment 正文时，content 为空字符串；仍保留引用关联
                if (!citationId) return null;
                return {
                  replyId: String(replyId),
                  citationId: String(citationId),
                  content: String(citationContent ?? ""),
                  documentId: documentId ? String(documentId) : undefined,
                  position: position ? String(position) : undefined,
                };
              })
              .filter(Boolean) as any[];
            if (bulkItems.length > 0) await saveBulkCreateConCitation(bulkItems);
          }
        } catch (e) {
          console.error("conreply/concitation during regenerate failed", e);
        }

        // 更新 AI 消息
        setItems((prev) =>
          prev.map((item) =>
            item.key === aiMessageKey
              ? {
                  ...item,
                  status: "success",
                  loading: false,
                  content: answerText || " ",
                  summary: summaryText ?? "",
                  rationale: rationaleText ?? "",
                  documents,
                  progress: progressSteps.length > 0 ? [...progressSteps] : item.progress,
                  extraInfo: {
                    ...(item.extraInfo || {}),
                    sessionLogId: sessionLogIdFromResp ? String(sessionLogIdFromResp) : undefined,
                    feedback: "default",
                  },
                }
              : item,
          ),
        );
      } catch (error) {
        console.error("regenerateMessage failed:", error);
        setItems((prev) =>
          prev.map((item) =>
            item.key === aiMessageKey
              ? {
                  ...item,
                  status: "error",
                  loading: false,
                  content: "抱歉，重新生成失败，请稍后再试。",
                }
              : item,
          ),
        );
      }
    },
    [
      selectedKeys,
      onlyUseSelectedSources,
      selectedSourceGuidNames,
      docCategoryIds,
      studySourceIds,
      includePersonalDocs,
      itemsRef,
      setItems,
    ],
  );

  return { regenerateMessage };
}
